/**
 * AccessibleToast — WCAG-compliant toast system.
 *
 * - Errors:   role="alert" (assertive), PERSIST until dismissed.
 * - Success:  role="status" (polite), 5-sec auto-dismiss.
 * - Warning:  role="alert" (assertive), PERSIST until dismissed.
 *
 * Per rcp-security §B.5 #3. Color is never the only signal — every toast
 * includes a severity word ("Error:", "Success:", "Warning:") + an icon
 * shape.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertCircle, CheckCircle2, AlertTriangle, X } from '@retail-ops/panel-shell/fi';

export type ToastKind = 'success' | 'error' | 'warn';

interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  message?: string;
}

interface ToastCtxValue {
  toasts: ReadonlyArray<Toast>;
  pushToast: (kind: ToastKind, title: string, message?: string) => void;
  dismissToast: (id: string) => void;
}

const ToastCtx = createContext<ToastCtxValue>({
  toasts: [],
  pushToast: () => {},
  dismissToast: () => {},
});

let toastIdCounter = 0;
function nextId(): string {
  toastIdCounter += 1;
  return `toast-${Date.now()}-${toastIdCounter}`;
}

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ReadonlyArray<Toast>>([]);
  const timersRef = useRef<Map<string, number>>(new Map());

  const dismissToast = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const pushToast = useCallback(
    (kind: ToastKind, title: string, message?: string) => {
      const toast: Toast = {
        id: nextId(),
        kind,
        title,
        ...(message ? { message } : {}),
      };
      setToasts((cur) => [...cur, toast]);
      if (kind === 'success') {
        const t = window.setTimeout(() => dismissToast(toast.id), 5000);
        timersRef.current.set(toast.id, t);
      }
    },
    [dismissToast],
  );

  const value = useMemo<ToastCtxValue>(
    () => ({ toasts, pushToast, dismissToast }),
    [toasts, pushToast, dismissToast],
  );

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastCtxValue {
  return useContext(ToastCtx);
}

function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: ReadonlyArray<Toast>;
  onDismiss: (id: string) => void;
}): JSX.Element | null {
  if (toasts.length === 0) return null;
  return (
    <div className="toast-stack" aria-label="Notifications">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}): JSX.Element {
  const role = toast.kind === 'success' ? 'status' : 'alert';
  const cls =
    toast.kind === 'success' ? 'toast toast-success'
      : toast.kind === 'error' ? 'toast toast-error'
        : 'toast toast-warn';
  const severityWord = toast.kind === 'success' ? 'Success:' : toast.kind === 'error' ? 'Error:' : 'Warning:';
  const Icon = toast.kind === 'success' ? CheckCircle2 : toast.kind === 'error' ? AlertCircle : AlertTriangle;
  return (
    <div className={cls} role={role} aria-live={toast.kind === 'success' ? 'polite' : 'assertive'}>
      <Icon aria-hidden="true" size={20} />
      <div style={{ flex: 1 }}>
        <strong>
          <span className="visually-hidden">{severityWord} </span>
          {toast.title}
        </strong>
        {toast.message ? <div>{toast.message}</div> : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label={`Dismiss ${severityWord.toLowerCase().replace(':', '')} notification`}
        className="btn-ghost"
        style={{ padding: 4, border: 'none' }}
      >
        <X aria-hidden="true" size={16} />
      </button>
    </div>
  );
}
